<template>
  <div
      class="stickersPanel editorPanel tw-left-2 tw-right-2 lg:tw-left-auto tw-bg-white tw-absolute tw-bottom-0 tw-transition tw-ease-out tw-duration-500 tw-rounded-t-[24px] lg:tw-rounded-tr-none lg:tw-rounded-l-[24px] lg:tw-w-[400px] lg:tw-right-0 lg:tw-bottom-auto lg:tw-top-1/2 lg:tw-translate-y-[-50%] tw-z-[2] tw-overflow-hidden"
      :class="{ panelHidden: !showStickersPanel }"
      :style="panelStyle"
  >
    <div ref="panelContentRef" class="tw-flex tw-flex-col tw-overflow-y-auto">
    <div class="textOptions tw-flex tw-justify-between tw-p-4 tw-px-8">
      <img
          :src="getAssetPath('/assets/images/collapseIcon.svg')"
          :alt="$t('closeButton')"
          @click="$emit('showStickersPanel', false)"
          class="tw-cursor-pointer"
      />
    </div>

    <div
      class="tw-overflow-x-auto tw-flex tw-mx-4 tw-mb-2 tw-gap-10 tw-pb-4"
      :class="scrollbarClasses"
    >
      <div
          v-for="(sticker, index) in stickers"
          :key="index"
          class="tw-cursor-pointer tw-flex-shrink-0"
          @click="handleStickerClick(sticker)"
      >
        <div
            class="tw-w-[90px] tw-h-[90px] tw-border-borderLight20 tw-border-2 tw-border-solid tw-rounded-xl tw-transition-all hover:tw-border-borderLight40 hover:tw-shadow-lg tw-p-2 tw-bg-bgDarkPanel tw-flex tw-items-center tw-justify-center tw-relative">
          <!-- Loading spinner -->
          <div v-if="loadingStickers[sticker.id]"
               class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
            <div class="spinner"></div>
          </div>
          <!-- Image -->
          <img
              :src="sticker.preview"
              :alt="sticker.name"
              class="tw-max-h-full tw-max-w-full tw-object-contain"
              :class="{ 'tw-opacity-0': loadingStickers[sticker.id] }"
              @load="onImageLoad(sticker.id)"
              @error="onImageError(sticker.id)"
          />
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import { getAssetPath } from "@/utils/assetPath";
import { getStickers } from "@/services/api";
import { scrollbarClasses } from "@/utils/tailwindScrollbarClasses";

export default {
  setup() {
    return {
      getAssetPath,
      scrollbarClasses
    };
  },
  props: {
    showStickersPanel: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return {
      stickers: [],
      loadingStickers: {},
      panelHeight: 0,
      resizeObserver: null,
    };
  },
  computed: {
    panelStyle() {
      const maxH = (typeof window !== "undefined" ? window.innerHeight : 800) * 0.64;
      const transformTransition = "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      const heightTransition = this.panelHeight ? ", height 0.4s ease-out" : "";
      return {
        height: this.panelHeight ? `${this.panelHeight}px` : "auto",
        maxHeight: `${maxH}px`,
        transition: transformTransition + heightTransition,
      };
    },
  },
  watch: {
    showStickersPanel(val) {
      if (val) {
        this.$nextTick(() => {
          this.updatePanelHeight();
          if (this.$refs.panelContentRef && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
            this.resizeObserver.observe(this.$refs.panelContentRef);
          }
        });
      }
    },
  },
  mounted() {
    this.getStickers();

    if (this.showStickersPanel) {
      this.$nextTick(() => {
        this.updatePanelHeight();
        if (this.$refs.panelContentRef && !this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
          this.resizeObserver.observe(this.$refs.panelContentRef);
        }
      });
    }
    nextTick();
  },
  beforeUnmount() {
    if (this.resizeObserver && this.$refs.panelContentRef) {
      this.resizeObserver.unobserve(this.$refs.panelContentRef);
    }
  },
  methods: {
    updatePanelHeight() {
      if (!this.$refs.panelContentRef || !this.showStickersPanel) return;
      const el = this.$refs.panelContentRef;
      const maxPx = (window.innerHeight * 64) / 100;
      this.panelHeight = Math.min(el.scrollHeight, maxPx);
    },
    async getStickers() {
      this.stickers = [];

      try {
        this.stickers = await getStickers();
        // Initialize loading state for all stickers
        const loadingState = {};
        this.stickers.forEach(sticker => {
          loadingState[sticker.id] = true;
        });
        this.loadingStickers = loadingState;
        this.$emit('stickersLoaded', this.stickers.length > 0);
        this.$nextTick(() => this.updatePanelHeight());
      } catch (error) {
        console.error("Error loading stickers:", error);
        this.$emit('stickersLoaded', false);
      }
    },

    onImageLoad(stickerId) {
      this.loadingStickers[stickerId] = false;
    },

    onImageError(stickerId) {
      this.loadingStickers[stickerId] = false;
      console.error(`Failed to load sticker image: ${stickerId}`);
    },

    handleStickerClick(sticker) {
      this.$emit('onStickerSelected', sticker);
      
      // Close panel on small screens (mobile devices)
      if (window.innerWidth < 1024) {
        this.$emit('showStickersPanel', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.stickersPanel {
  .textOptions {
    padding-left: 1.2rem;
  }

  .textOptions img {
    width: 40px;
    height: 40px;
    padding: 10px;
    box-sizing: border-box;
  }

  .tw-flex.tw-mx-4 {
    flex-wrap: wrap;
    gap: 0.5rem;
    overflow-x: visible;
    overflow-y: auto;
    max-height: 400px;

    > div {
      width: calc(33.333% - 0.35rem);
      flex-shrink: 0;

      @media (max-width: 1023px) {
        width: calc(33.333% - 0.35rem);
      }
    }
  }
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--color-sol-yellow);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
